import type { SOrdnerOverviewBundle } from "@/lib/sordnerOverview";
import { MetaGrid, MetaRow, OverviewCard } from "@/projectOverview/OverviewCard";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { SOrdnerHeroDocumentsSection } from "@/sordnerOverview/SOrdnerHeroDocumentsSection";
import { SOrdnerStandardMaterialSection } from "@/sordnerOverview/SOrdnerStandardMaterialSection";
import { SOrdnerVorlagenSection } from "@/sordnerOverview/SOrdnerVorlagenSection";

export function SOrdnerInfoSection({
  data,
  onRefetch,
}: {
  data: SOrdnerOverviewBundle;
  onRefetch: () => void;
}) {
  const { open: openProject } = useProjectOverview();
  const { sordner, location, sharepointUrl, projectCount, recentProjects } = data;

  return (
    <div className="space-y-4">
      <OverviewCard title="S-Ordner">
        <MetaGrid>
          <MetaRow label="Nummer" value={sordner.nummer} />
          <MetaRow label="Beschreibung" value={sordner.beschreibung || "—"} />
          <MetaRow
            label="Kunde"
            value={
              sordner.kunden_name
                ? sordner.kunden_code != null
                  ? `${sordner.kunden_name} (${sordner.kunden_code})`
                  : sordner.kunden_name
                : "—"
            }
          />
          <MetaRow label="Ort" value={location || "—"} />
          {sordner.jahr != null ? (
            <MetaRow label="Jahr (Stammdaten)" value={String(sordner.jahr)} />
          ) : null}
          {sordner.last_auftrag_nummer != null ? (
            <MetaRow
              label="Letzte Projektnummer"
              value={`${sordner.nummer}#${sordner.last_auftrag_nummer}`}
            />
          ) : null}
          <MetaRow label="Projekte in diesem S-Ordner" value={String(projectCount)} />
          {sharepointUrl ? (
            <MetaRow
              label="SharePoint"
              value={
                <a
                  href={sharepointUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Ordner in SharePoint öffnen
                </a>
              }
            />
          ) : sordner.sharepoint_folder_id ? (
            <MetaRow label="SharePoint" value="Verknüpft (URL wird geladen …)" />
          ) : (
            <MetaRow label="SharePoint" value="Noch nicht verknüpft" />
          )}
        </MetaGrid>
      </OverviewCard>

      <OverviewCard title="Projekte">
        {recentProjects.length === 0 ? (
          <p className="text-sm text-label-secondary">
            In diesem S-Ordner sind noch keine Projekte angelegt.
          </p>
        ) : (
          <ul className="divide-y divide-separator">
            {recentProjects.map((project) => (
              <li key={project.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm font-medium text-label">
                    {project.auftragsnummer}
                  </div>
                  <div className="truncate text-xs text-label-secondary">
                    {[project.kunde, project.beschreibung].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-accent hover:underline"
                  onClick={() => openProject(project.id)}
                >
                  Öffnen
                </button>
              </li>
            ))}
          </ul>
        )}
        {projectCount > recentProjects.length ? (
          <p className="mt-2 text-xs text-label-tertiary">
            Zeigt die neuesten {recentProjects.length} von {projectCount} Projekten.
          </p>
        ) : null}
      </OverviewCard>

      <SOrdnerStandardMaterialSection
        sordnerId={sordner.id}
        nummer={sordner.nummer}
        beschreibung={sordner.beschreibung}
      />

      <SOrdnerVorlagenSection sordnerId={sordner.id} />

      <SOrdnerHeroDocumentsSection
        sordnerId={sordner.id}
        currentHeroDocumentId={
          sordner.current_hero_document_id != null
            ? Number(sordner.current_hero_document_id)
            : null
        }
        onCurrentChanged={onRefetch}
      />
    </div>
  );
}
